import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Users, TrendingUp, Calendar, Check, ArrowRight, Sparkles, Loader2, History, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { getBillingSummary, getSeatUsage } from '../services/api';
import { toast } from 'sonner';
import { PLAN_PRICES, formatPrice, getAnnualSavings, calculateTotalPrice } from '../config/pricing';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  const [billing, setBilling] = useState(null);
  const [seats, setSeats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [seatsBillingCycle, setSeatsBillingCycle] = useState('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [stripeMode, setStripeMode] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [showSubscriptionDialog, setShowSubscriptionDialog] = useState(false);
  const [subscriptionAction, setSubscriptionAction] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [cancelStep, setCancelStep] = useState('confirm'); // 'confirm', 'offer', 'final'
  const [offerAccepted, setOfferAccepted] = useState(false);
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [billingRes, seatsRes, modeRes] = await Promise.all([
        getBillingSummary(),
        getSeatUsage(),
        axios.get(`${API}/api/billing/stripe-mode`)
      ]);
      setBilling(billingRes.data);
      setSeats(seatsRes.data);
      setStripeMode(modeRes.data);
      // Set initial billing cycles from subscription
      if (billingRes.data?.billing_cycle) {
        setBillingCycle(billingRes.data.billing_cycle);
      }
      if (billingRes.data?.seats_billing_cycle) {
        setSeatsBillingCycle(billingRes.data.seats_billing_cycle);
      }
    } catch (error) {
      console.error('Failed to fetch billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscription details
  const fetchSubscriptionDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/billing/subscription/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription details:', error);
    }
  };

  // Handle subscription actions
  const handleSubscriptionAction = async () => {
    if (!subscriptionAction) return;
    
    // For cancel action, handle multi-step flow
    if (subscriptionAction === 'cancel') {
      if (cancelStep === 'confirm') {
        // Move to offer step
        setCancelStep('offer');
        return;
      }
      
      if (cancelStep === 'offer' && offerAccepted) {
        // User accepted the retention offer - apply 25% discount for 6 months
        setActionLoading(true);
        try {
          const token = localStorage.getItem('token');
          // Create a retention promotion assignment
          await axios.post(`${API}/api/billing/apply-retention-offer`, {
            discount_percent: 25,
            duration_months: 6
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          toast.success('Great news! You now have 25% off for the next 6 months!');
          setShowSubscriptionDialog(false);
          resetCancelFlow();
          fetchData();
          fetchSubscriptionDetails();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to apply discount');
        } finally {
          setActionLoading(false);
        }
        return;
      }
    }
    
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      // Map undo_cancel to resume action on backend
      const backendAction = subscriptionAction === 'undo_cancel' ? 'resume' : subscriptionAction;
      const response = await axios.post(`${API}/api/billing/subscription/action`, {
        action: backendAction,
        reason: actionReason || undefined,
        cancel_at_period_end: cancelAtPeriodEnd
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      setShowSubscriptionDialog(false);
      resetCancelFlow();
      fetchData();
      fetchSubscriptionDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const resetCancelFlow = () => {
    setSubscriptionAction(null);
    setActionReason('');
    setCancelStep('confirm');
    setOfferAccepted(false);
  };

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Please check your email for confirmation.' });
      setShowPaymentResult(true);
      navigate('/billing', { replace: true });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/billing/checkout-status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus({ 
          status: 'success', 
          message: 'Payment successful! Your subscription has been updated.' 
        });
        setShowPaymentResult(true);
        toast.success('Payment successful!');
        fetchData(); // Refresh billing data
        navigate('/billing', { replace: true });
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ 
          status: 'expired', 
          message: 'Payment session expired. Please try again.' 
        });
        setShowPaymentResult(true);
        navigate('/billing', { replace: true });
        return;
      }

      // Continue polling if still pending
      setPaymentStatus({ status: 'pending', message: 'Verifying payment...' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setPaymentStatus({ 
          status: 'error', 
          message: 'Error verifying payment. Please contact support if charged.' 
        });
        setShowPaymentResult(true);
        navigate('/billing', { replace: true });
      }
    }
  }, [navigate]);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Payment was cancelled');
      // Clear URL params
      navigate('/billing', { replace: true });
    }
  }, [searchParams, navigate, pollPaymentStatus]);

  useEffect(() => {
    fetchData();
    fetchSubscriptionDetails();
  }, []);

  // Initiate Stripe checkout
  const initiateCheckout = async (packageId, quantity = 1) => {
    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;

      const response = await axios.post(`${API}/api/billing/create-checkout-session`, {
        package_id: packageId,
        quantity: quantity,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Fetch payment history
  const fetchPaymentHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/billing/payment-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentHistory(response.data.transactions || []);
      setShowHistoryDialog(true);
    } catch (error) {
      toast.error('Failed to load payment history');
    }
  };

  const seatUsagePercent = seats ? (seats.seats_used / seats.seats_included) * 100 : 0;
  const extraSeats = seats ? Math.max(0, seats.seats_used - seats.seats_included) : 0;
  
  const currentPlan = billing?.plan || 'professional';
  const planConfig = PLAN_PRICES[currentPlan] || PLAN_PRICES.professional;
  
  // Calculate prices based on billing cycles
  const planPrice = billingCycle === 'annual' 
    ? Math.round(planConfig.annual / 12) 
    : planConfig.monthly;
  const seatPrice = seatsBillingCycle === 'annual'
    ? Math.round(planConfig.extraSeatPrice.annual / 12)
    : planConfig.extraSeatPrice.monthly;
  const seatsTotal = extraSeats * seatPrice;
  const monthlyTotal = planPrice + seatsTotal;
  const annualSavings = getAnnualSavings(currentPlan);

  const handleUpdateBillingCycle = async (newCycle, type) => {
    try {
      const token = localStorage.getItem('token');
      const updateData = type === 'plan' 
        ? { billing_cycle: newCycle }
        : { seats_billing_cycle: newCycle };
      
      await axios.put(`${API}/api/billing/subscription`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (type === 'plan') {
        setBillingCycle(newCycle);
      } else {
        setSeatsBillingCycle(newCycle);
      }
      
      toast.success(`${type === 'plan' ? 'Plan' : 'Seats'} billing updated to ${newCycle}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update billing cycle');
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-white">Billing</h1>
          <p className="text-slate-400 mt-1">Manage your subscription, billing cycles, and seat usage</p>
        </div>
        {stripeMode && stripeMode.is_sandbox && (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-sm px-3 py-1">
            <AlertTriangle className="w-4 h-4 mr-1" />
            Sandbox Mode
          </Badge>
        )}
      </div>

      {/* Cancellation Status Alert */}
      {subscriptionDetails && subscriptionDetails.cancel_requested && (
        <Card className="border bg-red-500/10 border-red-500/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 text-red-400" />
                <div>
                  <p className="font-medium text-red-400">Cancellation Scheduled</p>
                  <p className="text-sm text-slate-400">
                    {subscriptionDetails.cancel_at_period_end 
                      ? 'Will be cancelled at end of billing period'
                      : 'Immediate cancellation requested'
                    }
                  </p>
                </div>
              </div>
              <Button 
                variant="outline"
                className="border-slate-600 hover:bg-slate-800"
                onClick={() => {
                  setSubscriptionAction('undo_cancel');
                  setShowSubscriptionDialog(true);
                }}
              >
                Undo Cancellation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Overview */}
      <Card className="bg-gradient-to-br from-slate-900 to-slate-900/50 border-slate-800">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <CreditCard className="w-8 h-8 text-blue-500" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-heading font-bold text-white">
                    {planConfig.name} Plan
                  </h2>
                  {billingCycle === 'annual' && (
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      Annual - Save {formatPrice(annualSavings)}/year
                    </Badge>
                  )}
                  {subscriptionDetails?.status && subscriptionDetails.status !== 'active' && (
                    <Badge className={
                      subscriptionDetails.status === 'paused' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                      subscriptionDetails.status === 'cancelled' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      'bg-slate-500/20 text-slate-400 border-slate-500/30'
                    }>
                      {subscriptionDetails.status.charAt(0).toUpperCase() + subscriptionDetails.status.slice(1)}
                    </Badge>
                  )}
                </div>
                <p className="text-slate-400">All 4 protection layers included</p>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-3xl font-heading font-bold text-white">
                  {formatPrice(monthlyTotal)}
                </p>
                <p className="text-sm text-slate-500">per month (effective)</p>
                {billingCycle === 'annual' && (
                  <p className="text-xs text-slate-600">
                    Billed {formatPrice(planConfig.annual)} annually
                  </p>
                )}
              </div>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setShowUpgradeDialog(true)}
              >
                {currentPlan === 'enterprise' ? 'Manage Plan' : 'Upgrade'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Billing Cycles */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Plan Billing Cycle */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading text-white">Plan Billing</CardTitle>
            <CardDescription className="text-slate-400">
              Choose how you want to pay for your base plan
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <Label htmlFor="plan-billing" className="text-slate-300">Pay annually</Label>
                  {billingCycle === 'annual' && (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                      2 months free!
                    </Badge>
                  )}
                </div>
                <Switch
                  id="plan-billing"
                  checked={billingCycle === 'annual'}
                  onCheckedChange={(checked) => handleUpdateBillingCycle(checked ? 'annual' : 'monthly', 'plan')}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className={`p-3 rounded-lg ${billingCycle === 'monthly' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-slate-800/30'}`}>
                  <p className="text-lg font-bold text-white">{formatPrice(planConfig.monthly)}</p>
                  <p className="text-xs text-slate-500">Monthly</p>
                </div>
                <div className={`p-3 rounded-lg ${billingCycle === 'annual' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/30'}`}>
                  <p className="text-lg font-bold text-white">{formatPrice(Math.round(planConfig.annual / 12))}</p>
                  <p className="text-xs text-slate-500">Annual (per mo)</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Seats Billing Cycle */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-heading text-white">Additional Seats Billing</CardTitle>
            <CardDescription className="text-slate-400">
              {extraSeats > 0 ? `You have ${extraSeats} additional seats` : 'Pay for extra users separately'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <Label htmlFor="seats-billing" className="text-slate-300">Pay annually</Label>
                  {seatsBillingCycle === 'annual' && extraSeats > 0 && (
                    <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                      Save {formatPrice(extraSeats * planConfig.extraSeatPrice.monthly * 2)}/year
                    </Badge>
                  )}
                </div>
                <Switch
                  id="seats-billing"
                  checked={seatsBillingCycle === 'annual'}
                  onCheckedChange={(checked) => handleUpdateBillingCycle(checked ? 'annual' : 'monthly', 'seats')}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className={`p-3 rounded-lg ${seatsBillingCycle === 'monthly' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-slate-800/30'}`}>
                  <p className="text-lg font-bold text-white">{formatPrice(planConfig.extraSeatPrice.monthly)}</p>
                  <p className="text-xs text-slate-500">Per seat/month</p>
                </div>
                <div className={`p-3 rounded-lg ${seatsBillingCycle === 'annual' ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/30'}`}>
                  <p className="text-lg font-bold text-white">{formatPrice(Math.round(planConfig.extraSeatPrice.annual / 12))}</p>
                  <p className="text-xs text-slate-500">Annual (per mo)</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cost Breakdown */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">Monthly Cost Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-slate-400">{planConfig.name} Plan ({billingCycle})</span>
              <span className="text-white font-medium">{formatPrice(planPrice)}/mo</span>
            </div>
            {extraSeats > 0 && (
              <div className="flex justify-between items-center py-2">
                <span className="text-slate-400">{extraSeats} additional seats ({seatsBillingCycle})</span>
                <span className="text-white font-medium">{formatPrice(seatsTotal)}/mo</span>
              </div>
            )}
            <div className="border-t border-slate-800 pt-3 flex justify-between items-center">
              <span className="text-white font-semibold">Total (effective monthly)</span>
              <span className="text-xl font-bold text-white">{formatPrice(monthlyTotal)}/mo</span>
            </div>
            {(billingCycle === 'annual' || seatsBillingCycle === 'annual') && (
              <div className="mt-4 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-emerald-400" />
                  <span className="text-emerald-400 font-medium">Annual savings applied!</span>
                </div>
                <p className="text-sm text-emerald-400/70 mt-1">
                  You are saving approximately {formatPrice(billing?.annual_savings || annualSavings)} per year with annual billing.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Seat Usage */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">Seat Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-slate-400">Used</span>
            <span className="text-white font-medium">
              {seats?.seats_used || 0} of {planConfig.seatsIncluded} included seats
            </span>
          </div>
          <Progress value={Math.min(seatUsagePercent, 100)} className="h-3" />
          {extraSeats > 0 && (
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <p className="text-amber-500 font-medium">
                {extraSeats} additional seats @ {formatPrice(seatPrice)}/seat/month
              </p>
              <p className="text-sm text-amber-400/70 mt-1">
                Additional charge: {formatPrice(seatsTotal)}/month
              </p>
            </div>
          )}
          <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800">
            <Users className="w-4 h-4 mr-2" />
            Add More Seats
          </Button>
        </CardContent>
      </Card>

      {/* Plan Features */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-white">Plan Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {planConfig.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/50">
                <Check className="w-4 h-4 text-emerald-500" />
                <span className="text-slate-300">{feature}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payment Method */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading text-white">Payment & Subscription</CardTitle>
            <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              Stripe Connected
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button 
              className="bg-blue-600 hover:bg-blue-700 w-full"
              onClick={() => {
                const packageId = `${currentPlan}_${billingCycle}`;
                initiateCheckout(packageId);
              }}
              disabled={checkoutLoading}
            >
              {checkoutLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              {checkoutLoading ? 'Processing...' : 'Pay Now with Stripe'}
            </Button>
            <Button 
              variant="outline" 
              className="border-slate-700 text-slate-300 hover:bg-slate-800 w-full"
              onClick={fetchPaymentHistory}
            >
              <History className="w-4 h-4 mr-2" />
              View Payment History
            </Button>
          </div>
          
          {/* Payment Info */}
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">S</span>
              </div>
              <div>
                <p className="font-medium text-white">Secure Payments via Stripe</p>
                <p className="text-sm text-slate-400">
                  {stripeMode?.is_sandbox ? 'Test mode - No real charges' : 'Credit card, debit card accepted'}
                </p>
              </div>
              {stripeMode?.is_sandbox && (
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 ml-auto">
                  Test Mode
                </Badge>
              )}
            </div>
            <p className="text-xs text-slate-500">
              You will be redirected to Stripe&apos;s secure checkout page to complete your payment.
            </p>
          </div>

          {/* Subscription Management */}
          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-sm font-medium text-slate-300 mb-3">Subscription Management</p>
            <div className="flex flex-wrap gap-2">
              {subscriptionDetails && !subscriptionDetails.cancel_requested && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  onClick={() => {
                    setSubscriptionAction('cancel');
                    setShowSubscriptionDialog(true);
                  }}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Cancel Subscription
                </Button>
              )}
              {subscriptionDetails && subscriptionDetails.cancel_requested && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                  onClick={() => {
                    setSubscriptionAction('undo_cancel');
                    setShowSubscriptionDialog(true);
                  }}
                >
                  Undo Cancellation
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Status Dialog */}
      <Dialog open={showPaymentResult} onOpenChange={setShowPaymentResult}>
        <DialogContent className="bg-slate-900 border-slate-800">
          <DialogHeader>
            <DialogTitle className="text-white text-xl flex items-center gap-2">
              {paymentStatus?.status === 'success' && <CheckCircle className="w-6 h-6 text-emerald-500" />}
              {paymentStatus?.status === 'pending' && <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />}
              {paymentStatus?.status === 'error' && <XCircle className="w-6 h-6 text-red-500" />}
              {paymentStatus?.status === 'expired' && <Clock className="w-6 h-6 text-amber-500" />}
              {paymentStatus?.status === 'timeout' && <Clock className="w-6 h-6 text-amber-500" />}
              Payment Status
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className={`text-center ${
              paymentStatus?.status === 'success' ? 'text-emerald-400' :
              paymentStatus?.status === 'pending' ? 'text-blue-400' :
              'text-slate-300'
            }`}>
              {paymentStatus?.message}
            </p>
          </div>
          {paymentStatus?.status !== 'pending' && (
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={() => setShowPaymentResult(false)}
            >
              Close
            </Button>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Payment History</DialogTitle>
            <DialogDescription className="text-slate-400">
              Your recent payment transactions
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {paymentHistory.length === 0 ? (
              <p className="text-center text-slate-400 py-8">No payment history found</p>
            ) : (
              paymentHistory.map((tx) => (
                <div key={tx.id} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-white">{tx.package_name}</p>
                      <p className="text-sm text-slate-400">
                        {new Date(tx.created_at).toLocaleDateString()} at{' '}
                        {new Date(tx.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-white">${tx.amount?.toFixed(2)}</p>
                      <Badge className={
                        tx.payment_status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' :
                        tx.payment_status === 'pending' ? 'bg-amber-500/10 text-amber-400' :
                        'bg-red-500/10 text-red-400'
                      }>
                        {tx.payment_status}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Action Dialog - Multi-step with Retention Offer */}
      <Dialog open={showSubscriptionDialog} onOpenChange={(open) => {
        setShowSubscriptionDialog(open);
        if (!open) resetCancelFlow();
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          {/* Cancel Flow - Step 1: Initial Confirmation */}
          {subscriptionAction === 'cancel' && cancelStep === 'confirm' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-xl flex items-center gap-2">
                  <XCircle className="w-6 h-6 text-red-400" />
                  Cancel Subscription
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  We&apos;re sorry to see you go. Are you sure you want to cancel?
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                    <input
                      type="radio"
                      id="cancel-end"
                      name="cancel-type"
                      checked={cancelAtPeriodEnd}
                      onChange={() => setCancelAtPeriodEnd(true)}
                      className="text-blue-500 w-4 h-4"
                    />
                    <label htmlFor="cancel-end" className="text-slate-300 cursor-pointer flex-1">
                      <span className="font-medium">Cancel at end of billing period</span>
                      <p className="text-sm text-slate-500">Continue using until your next billing date</p>
                    </label>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-800/50">
                    <input
                      type="radio"
                      id="cancel-immediate"
                      name="cancel-type"
                      checked={!cancelAtPeriodEnd}
                      onChange={() => setCancelAtPeriodEnd(false)}
                      className="text-blue-500 w-4 h-4"
                    />
                    <label htmlFor="cancel-immediate" className="text-slate-300 cursor-pointer flex-1">
                      <span className="font-medium">Cancel immediately</span>
                      <p className="text-sm text-slate-500">Lose access right away (no refund)</p>
                    </label>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Reason for cancellation (optional)</Label>
                  <Textarea
                    placeholder="Tell us why you're leaving..."
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    className="mt-2 bg-slate-800 border-slate-700 text-white"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowSubscriptionDialog(false);
                    resetCancelFlow();
                  }}
                  className="border-slate-700"
                >
                  Keep Subscription
                </Button>
                <Button 
                  className="bg-red-600 hover:bg-red-700"
                  onClick={handleSubscriptionAction}
                >
                  Continue to Cancel
                </Button>
              </div>
            </>
          )}

          {/* Cancel Flow - Step 2: Retention Offer */}
          {subscriptionAction === 'cancel' && cancelStep === 'offer' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-xl flex items-center gap-2">
                  <span className="text-2xl">🎁</span>
                  Wait! We have a special offer for you
                </DialogTitle>
              </DialogHeader>

              <div className="py-6">
                {/* Special Offer Card */}
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500/20 via-blue-500/20 to-purple-500/20 border border-emerald-500/30 p-6">
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    EXCLUSIVE
                  </div>
                  <div className="text-center">
                    <p className="text-emerald-400 text-sm font-medium mb-2">Stay with us and get</p>
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-5xl font-bold text-white">25%</span>
                      <span className="text-2xl text-slate-400">OFF</span>
                    </div>
                    <p className="text-lg text-white font-medium">For the next 6 months!</p>
                    <div className="mt-4 pt-4 border-t border-slate-700/50">
                      <p className="text-slate-400 text-sm">
                        That&apos;s a total savings of up to <span className="text-emerald-400 font-bold">${Math.round(299 * 0.25 * 6)}</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <p className="text-slate-300 text-center">
                    Keep all your protection features and save money. It&apos;s a win-win!
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <Button 
                  className="w-full bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white py-6 text-lg"
                  onClick={() => {
                    setOfferAccepted(true);
                    handleSubscriptionAction();
                  }}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  )}
                  Accept 25% Off - Keep My Subscription
                </Button>
                <Button 
                  variant="ghost"
                  className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                  onClick={() => {
                    setOfferAccepted(false);
                    handleSubscriptionAction();
                  }}
                  disabled={actionLoading}
                >
                  {actionLoading && !offerAccepted ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  No thanks, continue to cancel
                </Button>
              </div>
            </>
          )}

          {/* Undo Cancellation */}
          {subscriptionAction === 'undo_cancel' && (
            <>
              <DialogHeader>
                <DialogTitle className="text-white text-xl flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                  Keep Your Subscription
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Great choice! Your subscription will remain active.
                </DialogDescription>
              </DialogHeader>

              <div className="py-4">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <p className="text-emerald-400 font-medium">Welcome Back!</p>
                  <p className="text-sm text-slate-400 mt-1">
                    Your cancellation will be reversed and you&apos;ll continue to have full access to all features.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowSubscriptionDialog(false);
                    resetCancelFlow();
                  }}
                  className="border-slate-700"
                >
                  Close
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSubscriptionAction}
                  disabled={actionLoading}
                >
                  {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Undo Cancellation
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Choose Your Plan</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a plan that fits your organization needs
            </DialogDescription>
          </DialogHeader>
          
          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4 my-4 p-3 bg-slate-800/50 rounded-lg">
            <span className={`text-sm ${billingCycle === 'monthly' ? 'text-white font-medium' : 'text-slate-400'}`}>
              Monthly
            </span>
            <Switch
              checked={billingCycle === 'annual'}
              onCheckedChange={(checked) => setBillingCycle(checked ? 'annual' : 'monthly')}
            />
            <span className={`text-sm ${billingCycle === 'annual' ? 'text-white font-medium' : 'text-slate-400'}`}>
              Annual
            </span>
            {billingCycle === 'annual' && (
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                2 months free!
              </Badge>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {Object.entries(PLAN_PRICES).filter(([key]) => key !== 'trial').map(([key, plan]) => (
              <Card 
                key={key}
                className={`cursor-pointer transition-all ${
                  selectedPlan === key 
                    ? 'border-blue-500 bg-blue-500/10' 
                    : currentPlan === key
                    ? 'border-emerald-500/50 bg-emerald-500/5'
                    : 'border-slate-700 hover:border-slate-600'
                }`}
                onClick={() => setSelectedPlan(key)}
              >
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-white">{plan.name}</h3>
                      <p className="text-sm text-slate-400">{plan.description}</p>
                    </div>
                    {currentPlan === key && (
                      <Badge className="bg-emerald-600">Current</Badge>
                    )}
                  </div>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-white">
                      {formatPrice(billingCycle === 'annual' ? Math.round(plan.annual / 12) : plan.monthly)}
                    </span>
                    <span className="text-slate-400">/month</span>
                    {billingCycle === 'annual' && (
                      <p className="text-xs text-slate-500 mt-1">
                        Billed {formatPrice(plan.annual)} annually
                      </p>
                    )}
                  </div>
                  <ul className="space-y-2">
                    {plan.features.slice(0, 4).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!selectedPlan || checkoutLoading}
              onClick={() => {
                if (selectedPlan) {
                  const packageId = `${selectedPlan}_${billingCycle}`;
                  initiateCheckout(packageId);
                  setShowUpgradeDialog(false);
                }
              }}
            >
              {checkoutLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {selectedPlan === currentPlan ? 'Renew' : 'Upgrade to'} {PLAN_PRICES[selectedPlan || currentPlan]?.name}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
